import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchProjects } from "../../api";

import "aos/dist/aos.css";

export default function Projects({ isFullPage = false }) {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getProjects = async () => {
      try {
        const data = await fetchProjects();
        setProjects(data);
        
        // Extract unique categories
        const uniqueCats = ["All", ...new Set(data.map(p => p.category || "General"))];
        setCategories(uniqueCats);
        
        if (!isFullPage) {
          // Home page version: Show only latest 6, no filtering needed here
          setFilteredProjects(data.slice(0, 6));
        } else {
          setFilteredProjects(data);
        }
      } catch (error) {
        console.error("Failed to fetch projects", error);
      } finally {
        setLoading(false);
      }
    };
    getProjects();
  }, [isFullPage]);

  useEffect(() => {
    if (!isFullPage) return; // Don't filter on home page version

    if (activeCategory === "All") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => (p.category || "General") === activeCategory));
    }
  }, [activeCategory, projects, isFullPage]);

  return (
    <div className={`w-full ${isFullPage ? "py-20" : "py-12"}`} id="projects">
      <div className="mx-5 lg:mx-20 flex flex-col gap-10">
        <div className="text-center" data-aos="zoom-in">
          <h1
            className="font-Poppins font-medium text-slate-400 uppercase tracking-widest"
          >
            {isFullPage ? "Full Portfolio" : "Featured Projects"}
          </h1>
          <p
            className="text-gray-500 mt-3 font-Inter"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            {isFullPage 
              ? "Browse through all my creations and professional work." 
              : "A selection of some of the latest things I've built."}
          </p>
        </div>

        {/* Category Filters (Only on Full Page) */}
        {isFullPage && !loading && projects.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3" data-aos="fade-up">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#3aa1ff] text-white shadow-lg shadow-blue-500/20"
                    : "bg-white text-gray-500 border border-gray-100 hover:border-gray-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3aa1ff]"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20 text-gray-500 font-Nanum">
            No projects found.
          </div>
        ) : (
          <div
            data-aos="zoom-in"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col"
                data-aos="fade-up"
                data-aos-delay={`${index * 100}`}
              >
                <div className="h-48 bg-gray-100 relative overflow-hidden group">
                  {project.imageUrl ? (
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#131315]/5 text-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-12 h-12"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                        />
                      </svg>
                    </div>
                  )}
                  {project.link && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-[#3aa1ff] text-white px-6 py-2 rounded-full font-medium transform -translate-y-4 group-hover:translate-y-0 transition-all"
                      >
                        View Project
                      </a>
                    </div>
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-Poppins font-bold text-xl text-[#131315]">
                      {project.title}
                    </h3>
                    <span className="bg-[#3aa1ff]/10 text-[#3aa1ff] text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-tighter">
                      {project.category || "General"}
                    </span>
                  </div>
                  <p className="text-gray-600 font-Inter text-sm flex-1 leading-relaxed text-justify">
                    {project.description &&
                    project.description.split(" ").length > 50
                      ? project.description.split(" ").slice(0, 50).join(" ") +
                        "..."
                      : project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View More Button (Only on Home Page) */}
        {!isFullPage && !loading && projects.length > 6 && (
          <div className="flex justify-center mt-10" data-aos="fade-up">
            <Link
              to="/projects"
              className="bg-[#3aa1ff] text-white px-12 py-3.5 rounded-full font-Poppins font-semibold shadow-lg shadow-blue-500/25 hover:bg-[#288ce6] transition-all hover:shadow-blue-500/40 transform hover:-translate-y-1 active:scale-95"
            >
              View More
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
